import { supabase } from "@/integrations/supabase/client";

export interface Product {
  id: string;
  name: string;
  slug: string;
  price: number;
  regular_price?: number;
  sale_price?: number;
  description: string;
  short_description?: string;
  stock_quantity: number;
  stock_status: string;
  featured: boolean;
  sku?: string;
  created_at: string;
  updated_at: string;
}

export const productsStorage = {
  async getAll(): Promise<Product[]> {
    const { data, error } = await supabase
      .from("products")
      .select(`
        *,
        product_images(id, src, alt, position, product_id)
      `)
      .order("created_at", { ascending: false });

    if (error) throw error;
    
    // Sort images by position
    const productsWithSortedImages = data?.map(product => ({
      ...product,
      product_images: product.product_images?.sort((a: any, b: any) => a.position - b.position) || []
    }));
    
    return productsWithSortedImages || [];
  },

  async getById(id: string): Promise<Product | null> {
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .eq("id", id)
      .single();

    if (error) return null;
    return data;
  },

  async create(productData: {
    name: string;
    price: number;
    regular_price?: number;
    sale_price?: number;
    description: string;
    short_description?: string;
    stock_quantity: number;
    featured?: boolean;
    sku?: string;
  }): Promise<Product | null> {
    const slug = productData.name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    const { data, error } = await supabase
      .from("products")
      .insert([
        {
          ...productData,
          slug,
          stock_status: productData.stock_quantity > 0 ? "instock" : "outofstock",
          featured: productData.featured || false,
          wordpress_id: 0,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(
    id: string,
    productData: Partial<{
      name: string;
      price: number;
      regular_price?: number;
      sale_price?: number;
      description: string;
      short_description?: string;
      stock_quantity: number;
      featured: boolean;
      sku?: string;
    }>
  ): Promise<Product | null> {
    const updates: any = { ...productData };

    if (productData.name) {
      updates.slug = productData.name
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    }

    if (productData.stock_quantity !== undefined) {
      updates.stock_status = productData.stock_quantity > 0 ? "instock" : "outofstock";
    }

    const { data, error } = await supabase
      .from("products")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<boolean> {
    const { error } = await supabase.from("products").delete().eq("id", id);
    return !error;
  },

  async uploadImage(file: File, productId: string): Promise<string | null> {
    const fileExt = file.name.split(".").pop();
    const fileName = `${productId}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("product-images")
      .upload(fileName, file);

    if (uploadError) {
      console.error("Upload error:", uploadError);
      return null;
    }

    const {
      data: { publicUrl },
    } = supabase.storage.from("product-images").getPublicUrl(fileName);

    return publicUrl;
  },

  async addProductImage(productId: string, imageUrl: string, position: number = 0) {
    const { error } = await supabase.from("product_images").insert({
      product_id: productId,
      src: imageUrl,
      position,
    });

    if (error) throw error;
  },

  async getProductImages(productId: string) {
    const { data, error } = await supabase
      .from("product_images")
      .select("*")
      .eq("product_id", productId)
      .order("position");

    if (error) throw error;
    return data || [];
  },
};
